import { useLayoutEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Download, Search } from "lucide-react";
import { TOOLBOX_DOWNLOAD_PAGE_PATH, type ToolboxTitleBar } from "@ttc-spectree/toolbox-contract";
import {
  openCommandPalette,
  paletteShortcutLabel,
} from "@/components/grooming/use-command-registry";
import { useMe } from "@/hooks/queries/use-me";
import { useNextUpProject } from "@/hooks/queries/use-next-up";
import { isSharedAgendaPath } from "@/lib/grooming/paths";
import { TOOLBOX_HOME_PATH, toolFromPath } from "@/lib/toolbox/tools";
import { updateReady, useUpdateNotice } from "@/lib/toolbox/update-notice";
import { ClaudeChip } from "./claude-chip";
import { ProjectSwitcher } from "./project-switcher";
import { QueueChip } from "./queue-chip";
import { ToolSwitcher } from "./tool-switcher";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * Whether the title bar shows Grooming's project switcher and search: never
 * on Formulator pages, the FogBugz event log summary or the FogBugz
 * Formulator (not about an ADO project; epics PI-SPEC-SW-E307, E309), nor on
 * Product Q&A (it has its own product picker; E310), nor on FogBugz to ADO
 * (the case decides the ADO project; E311); on the Toolbox home and setup pages only while
 * `groomingVisible` (a Toolbox role on some project, or Admin; E283) is not
 * known to be false; on the shared agenda (open to a person with no role,
 * PKG-PM-C) not once it is known to be false. Pure; exported for tests.
 */
export function groomingControlsShown(
  pathname: string,
  groomingVisible: boolean | undefined
): boolean {
  const tool = toolFromPath(pathname);
  if (
    tool === "formulator" ||
    tool === "fogbugz" ||
    tool === "fogbugz-formulator" ||
    tool === "ask" ||
    tool === "fogbugz-to-ado"
  ) {
    return false;
  }
  if (isSharedAgendaPath(pathname) && groomingVisible === false) return false;
  return tool === "grooming" || groomingVisible !== false;
}

/** The search box's words: "Search <project>" when it searches one project, else the general words. Pure; exported for tests. */
export function searchPlaceholder(searchProject: string | undefined): string {
  return searchProject ? `Search ${searchProject}` : "Search items or jump to…";
}

/** Title bar height (design `.tbar`) when the app does not report one. */
export const TITLE_BAR_HEIGHT_PX = 42;

interface TitleBarProps {
  /** Contract 2 window frame; undefined for a contract-1 app (native frame, nothing reserved). */
  titleBar: ToolboxTitleBar | undefined;
  project: string | undefined;
  /** The app's own semver (`window.spectreeToolbox.app.version`), shown beside the brand. */
  appVersion: string | undefined;
}

/**
 * The SpecTree Toolbox title bar (epic PI-SPEC-SW-E280; design `.tbar`):
 * brand with the app's version, tool switcher, project switcher, a spacer,
 * the search that opens the command palette, the run-queue chip (epic
 * PI-SPEC-SW-E284; absent without a contract-3 app or an empty queue) and the
 * Claude chip.
 *
 * The search is in the row's flow and gives way first (UX-PM-22, frame 1a:
 * a 320 px basis, at least 120 px); below 1320 px it collapses to its icon and
 * shortcut. Its flex-shrink is 1000 against the project switcher's 1, so the
 * project name keeps its full width while the search can still shrink and is
 * the last thing to give way: only once the search is at its minimum or
 * collapsed does a long name (ADO allows 64 characters) truncate with an
 * ellipsis, its full text in a tooltip. Brand, tool switcher, chips and the
 * reserved window-control spacers never shrink, so nothing is pushed under the
 * native window controls.
 *
 * The native window controls stay the OS's own (traffic lights on macOS, the
 * Windows title-bar overlay); the bar keeps `reservedLeft` / `reservedRight`
 * pixels free for them. Those numbers come from the app at run time, so they
 * are set as CSS variables on the bar and read by Tailwind arbitrary values.
 * The bar is a window drag region; every control in it is not.
 *
 * The project switcher and the search are Grooming's (epic PI-SPEC-SW-E282):
 * they are hidden on Formulator pages, and on the Toolbox home and setup
 * pages once `/me` says the person holds no Toolbox role (`groomingVisible`).
 *
 * "Update ready" (epic PI-SPEC-SW-E288, feature PI-SPEC-SW-1468, frame 1g):
 * while the app reports a newer version it has not installed (an unsigned
 * pilot build), a blue pill after the spacer and before the search links
 * SpecTree's download page, on every Toolbox page including the Formulator's.
 * It never shrinks; it goes away once the new version runs.
 *
 * The search names the project it searches (epic PI-SPEC-SW-E292, feature
 * PI-SPEC-SW-1499, UX-PM-33): "Search <project>" on a project's pages and, on
 * Toolbox home, for home's current project (`useNextUpProject`: the last
 * project shown while the person may still read it), the project the command
 * palette looks items up in there. Elsewhere it keeps "Search items or jump to…".
 */
export function TitleBar({ titleBar, project, appVersion }: TitleBarProps) {
  const barRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const { data: me } = useMe();
  const showGroomingControls = groomingControlsShown(pathname, me?.groomingVisible);
  const showUpdateReady = updateReady(useUpdateNotice());
  const homeProject = useNextUpProject();
  const searchProject = project ?? (pathname === TOOLBOX_HOME_PATH ? homeProject : undefined);
  const searchWords = searchPlaceholder(searchProject);
  const reservedLeft = titleBar?.reservedLeft ?? 0;
  const reservedRight = titleBar?.reservedRight ?? 0;
  const height = titleBar?.height && titleBar.height > 0 ? titleBar.height : TITLE_BAR_HEIGHT_PX;

  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    bar.style.setProperty("--toolbox-reserved-left", `${String(reservedLeft)}px`);
    bar.style.setProperty("--toolbox-reserved-right", `${String(reservedRight)}px`);
    bar.style.setProperty("--toolbox-title-bar-height", `${String(height)}px`);
  }, [reservedLeft, reservedRight, height]);

  return (
    <header
      ref={barRef}
      className="flex h-[var(--toolbox-title-bar-height,42px)] flex-none select-none items-center gap-2.5 border-b border-[#1F1F23] bg-[#0B0B0D] px-3 [-webkit-app-region:drag]"
      data-testid="toolbox-title-bar"
      data-reserved-left={reservedLeft}
      data-reserved-right={reservedRight}
    >
      {reservedLeft > 0 && (
        <span
          className="-ml-3 h-full w-[var(--toolbox-reserved-left)] flex-none"
          data-testid="title-bar-reserved-left"
          aria-hidden="true"
        />
      )}
      <span className="flex flex-none items-center gap-2 whitespace-nowrap font-display text-[13px] font-bold tracking-[-0.01em] text-[#FAFAFA]">
        <span className="h-2 w-2 rounded-[2px] bg-[#E11837]" aria-hidden="true" />
        SpecTree Toolbox
        {appVersion && (
          <span
            className={cn("font-sans text-[11px] font-medium tracking-normal", TOOLBOX_TEXT.faint)}
            data-testid="title-bar-app-version"
          >
            v{appVersion}
          </span>
        )}
      </span>
      <ToolSwitcher className="flex-none whitespace-nowrap" />
      {showGroomingControls && <ProjectSwitcher project={project} />}
      <span className="flex-1" data-testid="title-bar-spacer" />
      {showUpdateReady && (
        <Link
          to={TOOLBOX_DOWNLOAD_PAGE_PATH}
          className="inline-flex h-[26px] flex-none items-center gap-1.5 whitespace-nowrap rounded-full bg-[#1D4ED8] px-2.5 text-xs font-semibold text-white transition-colors hover:bg-[#2563EB] [-webkit-app-region:no-drag]"
          data-testid="update-ready-chip"
        >
          <Download className="h-[13px] w-[13px] flex-none" aria-hidden="true" />
          Update ready
        </Link>
      )}
      {showGroomingControls && (
        <button
          type="button"
          onClick={openCommandPalette}
          className={cn("flex h-7 min-w-0 flex-[0_1000_320px] items-center gap-2 rounded-lg border border-[#1F1F23] bg-[#121215] px-2.5 text-[13px] transition-colors hover:border-[#2A2A2E] hover:text-[#A1A1AA] [-webkit-app-region:no-drag] max-[1320px]:flex-none max-[1320px]:px-2 min-[1320px]:min-w-[120px]", TOOLBOX_TEXT.faint)}
          aria-label={searchWords.replace(/…$/, "")}
          data-testid="title-bar-search"
        >
          <Search className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
          <span className="flex-1 truncate text-left max-[1320px]:hidden" data-testid="title-bar-search-words">
            {searchWords}
          </span>
          <kbd className="flex-none whitespace-nowrap rounded-[5px] border border-[#27272A] bg-[#0E0E10] px-1.5 py-px font-sans text-[11px] font-medium text-[#A1A1AA]">
            {paletteShortcutLabel(titleBar?.platform)}
          </kbd>
        </button>
      )}
      <QueueChip />
      <ClaudeChip project={project} />
      {reservedRight > 0 && (
        <span
          className="-mr-3 h-full w-[var(--toolbox-reserved-right)] flex-none"
          data-testid="title-bar-reserved-right"
          aria-hidden="true"
        />
      )}
    </header>
  );
}
